import { useState, useEffect, useCallback, useMemo, memo, lazy, Suspense } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../formElements/Button";
import { RedDotIcon } from "../svgElements/RedDotIcon";
import { ROUTES } from "../../constants/routes";
import ProfilePlaceholder from "../../assets/images/user-placeholder.png";
import MenuIcon from "../svgElements/MenuIcon";
const NotificationPanel = lazy(() => import("./NotificationPanel"));
import { useAppSelector } from "../../redux/hooks";
import { getNotifications } from "../../services/notification";
import { captureSentryException } from "../../utils/sentry";
import "../../styles/header.scss";

interface HeaderProps {
  toggleSidebar?: () => void;
}

const Header = ({ toggleSidebar }: HeaderProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const userInfo = useAppSelector((state) => state.auth.user);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const handleUnreadCountChange = useCallback((count: number) => {
    setUnreadCount(count);
  }, []);

  // Fetch initial unread count on mount
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await getNotifications({ page: 1, limit: 1 });
        if (response.success && response.data) {
          setUnreadCount(response.data.unreadCount);
        }
      } catch (error) {
        captureSentryException(error, { context: "Header.fetchUnreadCount" });
      }
    };
    fetchUnreadCount();
  }, []);

  const headerContent = useMemo(() => {
    if (pathname === ROUTES.DASHBOARD)
      return {
        title: "Dashboard Overview",
        subtitle: "Monitor your CannaScan platform performance",
      };
    if (pathname === ROUTES.USERS)
      return {
        title: "User Management",
        subtitle: "Manage and monitor all registered users",
      };
    if (pathname === ROUTES.SETTINGS)
      return {
        title: "Settings",
        subtitle: "Manage your information",
      };
    if (pathname === ROUTES.REPORTS_LOGS)
      return {
        title: "Reports & Activity Logs",
        subtitle: "View and export system activity logs",
      };
    return {
      title: "Cannabis Admin",
      subtitle: "Admin Dashboard",
    };
  }, [pathname]);

  return (
    <header className="header">
      <div className="header-left">
        <Button
          className="clear-btn p-0 menu-toggle-btn"
          onClick={toggleSidebar}
        >
          <MenuIcon />
        </Button>
        <div className="header-title-section">
          <h3 className="header-title">{headerContent.title}</h3>
          <p className="header-subtitle">{headerContent.subtitle}</p>
        </div>
      </div>

      <div className="header-right">
        <div className="notification-wrapper">
          <Button className="clear-btn p-0 notification-btn" onClick={() => setShowNotifications(!showNotifications)}>
            <RedDotIcon unreadCount={unreadCount} />
          </Button>
          <Suspense fallback={null}>
            <NotificationPanel
              isOpen={showNotifications}
              onClose={() => setShowNotifications(false)}
              onUnreadCountChange={handleUnreadCountChange}
            />
          </Suspense>
        </div>

        {userInfo && (
          <div className="user-profile" onClick={() => navigate(ROUTES.SETTINGS)}>
            <img
              src={userInfo.profileImage || ProfilePlaceholder}
              alt="avatar"
              className="user-avatar"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = ProfilePlaceholder;
              }}
            />
            <div className="user-info">
              <h4 className="user-name">{userInfo.fullName}</h4>
              <p className="user-email">{userInfo.email}</p>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default memo(Header);
